import z from "zod";

import { USER_REGISTRATION_METHOD_SCHEMA } from "../schema/UserRegistrationMethodSchema";
import {
  authedServiceRequest,
  authToken,
  serviceResponse,
} from "./BaseService";

/** ******************************************************************************
 *  Types
 ******************************************************************************* */

export const UserSchema = z.object({
  _id: z.string(),
  email: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  role: z.string().nullable(),
  passwordHash: z.string().nullable(),
  resetToken: z.string().nullable(),
  resetTokenExpiresAt: z.date().nullable(),
  stripeCustomerId: z.string().nullable(),
  emailVerifiedAt: z.date().nullable(),
  emailVerificationToken: z.string().uuid().nullable(),
  emailVerificationTokenRequestedAt: z.date().nullable(),
  emailVerificationRequired: z.boolean(),
  isDisabled: z.boolean(),
  username: z.string().nullable(),
  waitlistedAt: z.date().nullable(),
  registrationMethod: z.enum(USER_REGISTRATION_METHOD_SCHEMA),
  githubUsername: z.string().nullable(),
  hasCompletedSurvey: z.boolean(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export const UserMetricsSchema = UserSchema;

/**
 * Sensitive fields are omitted from API responses that are returned to the
 * client. Note that this requires using these Zod objects to parse the response
 * data in the respective service methods.
 */
export const ApiResponseUserSchema = UserSchema.omit({
  passwordHash: true,
  resetToken: true,
  resetTokenExpiresAt: true,
  emailVerificationToken: true,
  stripeCustomerId: true,
}).merge(
  z.object({
    hasPurchasedCredits: z.boolean(),
  }),
);

/**
 * Safe projection of a user for the super-admin user-detail page. A strict
 * whitelist (parsed, not just typed) so that sensitive columns — password hash,
 * reset/verification tokens, Stripe customer id — can never reach the browser,
 * even if new sensitive fields are added to `UserSchema` later.
 */
export const UserAdminSummarySchema = UserSchema.pick({
  _id: true,
  email: true,
  firstName: true,
  lastName: true,
  role: true,
  username: true,
  githubUsername: true,
  registrationMethod: true,
  emailVerifiedAt: true,
  waitlistedAt: true,
  isDisabled: true,
  createdAt: true,
  updatedAt: true,
});

export const authenticationResponse = serviceResponse.merge(
  z.object({
    user: ApiResponseUserSchema.optional(),
    token: authToken.optional(),
    apiKeyId: z.string().optional(),
    teamId: z.string().optional(),
  }),
);

export type User = z.infer<typeof UserSchema>;
export type ApiResponseUser = z.infer<typeof ApiResponseUserSchema>;
export type UserAdminSummary = z.infer<typeof UserAdminSummarySchema>;
export type UserMetrics = z.infer<typeof UserMetricsSchema>;
export type AuthenticationResponse = z.infer<typeof authenticationResponse>;

export type UserServiceInterface = {
  get(request: GetUserRequest): Promise<GetUserResponse>;
  getUserAdminSummary: (userId: string) => Promise<UserAdminSummary>;
  superList(request: SuperListUsersRequest): Promise<SuperListUsersResponse>;
};

/** ******************************************************************************
 *  Get User
 ******************************************************************************* */

export const getUserParams = z.undefined();

export const getUserRequest = authedServiceRequest.merge(
  z.object({
    params: getUserParams,
  }),
);

export const getUserResponse = serviceResponse.merge(
  z.object({
    user: ApiResponseUserSchema.nullish(),
  }),
);

export type GetUserParams = z.infer<typeof getUserParams>;
export type GetUserRequest = z.infer<typeof getUserRequest>;
export type GetUserResponse = z.infer<typeof getUserResponse>;

/** ******************************************************************************
 *  Super List Users
 ******************************************************************************* */

/**
 * Columns the super-admin user list can be ordered by. A closed set because the
 * value reaches a SQL ORDER BY — the transport must only be able to name a column
 * that exists, and adding one has to force the store's mapping to be updated.
 */
export const SUPER_LIST_USERS_SORT_KEYS = [
  "createdAt",
  "email",
  "firstName",
  "lastName",
  "role",
] as const;

export const superListUsersSortSchema = z.object({
  key: z.enum(SUPER_LIST_USERS_SORT_KEYS),
  direction: z.enum(["asc", "desc"]),
});

export type SuperListUsersSortKey = (typeof SUPER_LIST_USERS_SORT_KEYS)[number];
export type SuperListUsersSort = z.infer<typeof superListUsersSortSchema>;

/** Newest-first, matching what the list showed before it was sortable. */
export const DEFAULT_SUPER_LIST_USERS_SORT: SuperListUsersSort = {
  key: "createdAt",
  direction: "desc",
};

export const superListUsersParams = z.object({
  _id: z.string().optional(),
  email: z.string().optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  username: z.string().optional(),
  cursor: z
    .object({
      pageSize: z.number(),
      offset: z.number(),
    })
    .nullish(),
  sort: superListUsersSortSchema.nullish(),
});

export const superListUsersRequest = authedServiceRequest.merge(
  z.object({
    params: superListUsersParams,
  }),
);

export const superListUsersResponse = serviceResponse.merge(
  z.object({
    users: z.array(UserMetricsSchema).optional(),
    total: z.number().optional(),
  }),
);

export type SuperListUsersParams = z.infer<typeof superListUsersParams>;
export type SuperListUsersRequest = z.infer<typeof superListUsersRequest>;
export type SuperListUsersResponse = z.infer<typeof superListUsersResponse>;

/** ******************************************************************************
 *  Onboarding Survey
 ******************************************************************************* */

export type UserSurvey = {
  whoAreYou?: string;
  aiProvidersYouUse?: string[];
  monthlyAiSpend?: string;
  aiUseCases?: string[];
  howDidYouHearAboutUs?: string;
};
